import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  forwardRef,
  memo,
} from "react";
import WaveSurfer from "wavesurfer.js";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  PlayActiveLightIcon,
  PauseActiveLightIcon,
  PlayIconActiveDark,
} from "../../../assets/icon";
import { audioPlayerStyles } from "./styles";
import { useAppStore } from "../../../stores/appStore";
import { useAuthenticatedFileUrl } from "../../../hooks/useAuthenticatedFileUrl";

interface WaveformProps {
  audioUrl: string;
  theme: any;
  playbackRate: number;
}

const Waveform = memo(
  forwardRef<WaveSurfer | null, WaveformProps>(
    ({ audioUrl, theme, playbackRate }, ref) => {
      const containerRef = useRef<HTMLDivElement>(null);
      const waveSurferRef = useRef<WaveSurfer | null>(null);
      const prevTheme = useRef(theme.palette.mode);

      const createWS = useCallback(() => {
        return WaveSurfer.create({
          container: containerRef.current!,
          waveColor: theme.palette.audioPlayer.waveColorEnd,
          progressColor: theme.palette.audioPlayer.waveColorStart,
          cursorWidth: 2,
          cursorColor: "#E6E6E680",
          height: 50,
          barWidth: 3,
          barGap: 3,
          barRadius: 2,
          backend: "MediaElement",
          fillParent: true,
          dragToSeek: true,
        });
      }, [
        theme.palette.audioPlayer.waveColorEnd,
        theme.palette.audioPlayer.waveColorStart,
      ]);

      useEffect(() => {
        if (!containerRef.current) {
          return;
        }

        waveSurferRef.current = createWS();

        waveSurferRef.current.load(audioUrl).catch((err: unknown) => {
          if ((err as DOMException).name !== "AbortError") {
            console.error(`WaveSurfer load error for ${audioUrl}:`, err);
          }
        });

        if (ref) {
          typeof ref === "function"
            ? ref(waveSurferRef.current)
            : (ref.current = waveSurferRef.current);
        }
        return () => {
          waveSurferRef.current?.destroy();
        };
      }, [audioUrl, ref, createWS]);

      useEffect(() => {
        if (!waveSurferRef.current) {
          return;
        }

        if (prevTheme.current === theme.palette.mode) {
          waveSurferRef.current.setPlaybackRate(playbackRate);
          return;
        }

        const wsOld = waveSurferRef.current;
        const wasPlaying = wsOld.isPlaying();
        const curTime = wsOld.getCurrentTime();
        const dur = wsOld.getDuration();

        try {
          wsOld.destroy();
        } catch (err: unknown) {
          if ((err as DOMException).name !== "AbortError") {
            console.error("Error destroying WaveSurfer:", err);
          }
        }

        waveSurferRef.current = createWS();
        waveSurferRef.current.load(audioUrl).catch((err: unknown) => {
          if ((err as DOMException).name !== "AbortError") {
            console.error(`WaveSurfer reload error for ${audioUrl}:`, err);
          }
        });

        if (ref) {
          typeof ref === "function"
            ? ref(waveSurferRef.current)
            : (ref.current = waveSurferRef.current);
        }

        waveSurferRef.current.on("ready", () => {
          if (dur) waveSurferRef.current!.seekTo(curTime / dur);
          waveSurferRef.current!.setPlaybackRate(playbackRate);
          if (wasPlaying) {
            waveSurferRef.current!.play();
          }
        });

        prevTheme.current = theme.palette.mode;
      }, [theme.palette.mode, audioUrl, playbackRate, ref, createWS]);

      return <Box ref={containerRef} sx={{ width: { xs: 174, sm: 224 } }} />;
    }
  )
);

interface RecordingPlayerProps {
  audioUrl: string;
  title: string;
  timestamp: string;
  isPlaying: boolean;
  onPlay: () => void;
  onLoadingError: () => void;
}

export const RecordingPlayer: React.FC<RecordingPlayerProps> = ({
  audioUrl,
  title,
  timestamp,
  isPlaying,
  onPlay,
  onLoadingError,
}) => {
  const { url: resolvedAudioUrl, isLoading: isResolvingAudioUrl } =
    useAuthenticatedFileUrl(audioUrl);
  const effectiveAudioUrl = resolvedAudioUrl ?? "";
  const theme = useTheme();
  const styles = audioPlayerStyles(theme);
  const waveSurferRef = useRef<WaveSurfer | null>(null);
  const { isDark } = useAppStore();

  const [isCurrentlyPlaying, setIsCurrentlyPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [playStart, setPlayStart] = useState<number | null>(null);
  const [isMini, setIsMini] = useState(false);

  const [useFallback, setUseFallback] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const [showLoading, setShowLoading] = useState(false);
  const loadingTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (window.MediaRecorder) {
      try {
        [
          "audio/mp4;codecs=mp4a.40.2",
          "audio/mp4",
          "audio/webm;codecs=opus",
          "audio/webm",
          "audio/ogg;codecs=opus",
          "audio/ogg",
          "audio/mp3",
        ].filter((type) => MediaRecorder.isTypeSupported(type));
      } catch (err) {
        console.error("Error checking MediaRecorder support:", err);
      }
    }
  }, [effectiveAudioUrl]);

  useEffect(() => {
    setIsLoaded(false);
    setShowLoading(false);
    setUseFallback(false);
    if (loadingTimeoutRef.current !== null) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, [effectiveAudioUrl]);

  useEffect(() => {
    if (isLoaded || useFallback) {
      setShowLoading(false);
      if (loadingTimeoutRef.current !== null) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      return;
    }

    loadingTimeoutRef.current = window.setTimeout(() => {
      setShowLoading(true);
    }, 500);

    return () => {
      if (loadingTimeoutRef.current !== null) {
        window.clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  }, [isLoaded, useFallback]);

  useEffect(() => {
    if (isLoaded || useFallback) return;
    timeoutRef.current = window.setTimeout(() => {
      if (!isLoaded) {
        onLoadingError();
        setUseFallback(true);
        setShowLoading(false);
      }
    }, 15000);
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [isLoaded, useFallback, onLoadingError]);

  const fmt = (sec: number) =>
    `${Math.floor(sec / 60)}:${Math.floor(sec % 60)
      .toString()
      .padStart(2, "0")}`;

  const onSpeed = (e: SelectChangeEvent<number>) => {
    const r = Number(e.target.value);
    setPlaybackRate(r);
    waveSurferRef.current?.setPlaybackRate(r);
  };

  const onPlayPause = useCallback(() => {
    if (!isLoaded || !waveSurferRef.current) {
      return;
    }

    waveSurferRef.current.playPause();
    const now = !isCurrentlyPlaying;
    setIsCurrentlyPlaying(now);

    if (now) {
      setPlayStart(Date.now());
      setIsMini(true);
      onPlay();
    } else if (playStart) {
      setPlayStart(null);
    }
  }, [isLoaded, isCurrentlyPlaying, playStart, onPlay]);

  useEffect(() => {
    const ws = waveSurferRef.current;
    if (!ws) {
      return;
    }

    setCurrentTime(0);

    const ready = () => {
      const duration = ws.getDuration();
      setDuration(duration);
      setIsLoaded(true);
      ws.setPlaybackRate(playbackRate);
    };
    const process = () => setCurrentTime(ws.getCurrentTime());
    const finish = () => {
      setIsCurrentlyPlaying(false);
      setCurrentTime(0);
      if (isMini) {
        setIsMini(false);
      }
      if (playStart) {
        setPlayStart(null);
      }
    };

    const drag = (relativeX: number) => {
      setCurrentTime(relativeX * ws.getDuration());
    };

    const error = (err: any) => {
      console.error("WaveSurfer error:", err);
      onLoadingError();
      if (!isLoaded) {
        setUseFallback(true);
      }
    };

    ws.on("ready", ready);
    ws.on("audioprocess", process);
    ws.on("finish", finish);
    ws.on("drag", drag);
    ws.on("error", error);

    return () => {
      ws.un("ready", ready);
      ws.un("audioprocess", process);
      ws.un("finish", finish);
      ws.un("drag", drag);
      ws.un("error", error);
    };
  }, [playbackRate, isMini, playStart, isLoaded, effectiveAudioUrl, onLoadingError]);

  useEffect(() => {
    const h = (e: PromiseRejectionEvent) => {
      if ((e.reason as DOMException)?.name === "AbortError") {
        e.preventDefault();
      } else {
        console.error("Unhandled promise rejection:", e.reason);
      }
    };
    window.addEventListener("unhandledrejection", h);
    return () => window.removeEventListener("unhandledrejection", h);
  }, []);

  useEffect(() => {
    if (!isPlaying) {
      setIsCurrentlyPlaying(false);
      waveSurferRef.current?.pause();

      if (playStart) {
        setPlayStart(null);
      }
    }
  }, [isPlaying, playStart]);

  if (isResolvingAudioUrl || !effectiveAudioUrl) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (useFallback) {
    return (
      <Box sx={{ ...styles.container, maxWidth: "300px", padding: "10px" }}>
        <audio
          controls
          preload="metadata"
          src={effectiveAudioUrl}
          style={{ width: "250px" }}
          onCanPlay={() => {
            setIsLoaded(true);
          }}
          onError={(e) => {
            const audio = e.target as HTMLAudioElement;
            console.error("Native audio player error:", audio.error);
            onLoadingError();
          }}
          onPlay={() => {
            setPlayStart(Date.now());
          }}
          onPause={() => {
            if (playStart) {
              setPlayStart(null);
            }
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.container} position="relative">
      {!isLoaded && showLoading && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255,255,255,0.9)",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            transition: "opacity 0.3s ease-in-out",
          }}
        >
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 1, fontSize: 12 }}>
            Loading audio...
          </Typography>
        </Box>
      )}

      <Box sx={styles.header}>
        <Box sx={styles.labelsContainer}>
          <Typography variant="body2" sx={styles.text}>
            "{title || "Voice Message"}"
          </Typography>

          {timestamp && (
            <Typography variant="body2" sx={styles.text}>
              {timestamp}
            </Typography>
          )}
        </Box>

        <FormControl size="small" sx={{ minWidth: 60 }}>
          <Select
            value={playbackRate}
            onChange={onSpeed}
            disabled={!isLoaded}
            sx={styles.speedSelect}
          >
            {[0.5, 1, 1.5, 2].map((v) => (
              <MenuItem key={v} value={v} sx={{ fontSize: 14 }}>
                {v}x
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={styles.audioControlsContainer}>
        <Box sx={styles.playButton} onClick={onPlayPause}>
          {isLoaded &&
            (!isCurrentlyPlaying ? (
              isDark ? (
                <PlayIconActiveDark style={{ width: 48, height: 48 }} />
              ) : (
                <PlayActiveLightIcon style={{ width: 48, height: 48 }} />
              )
            ) : (
              <PauseActiveLightIcon style={{ width: 48, height: 48 }} />
            ))}
        </Box>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ duration: 0.5 }}
        >
          <Waveform
            ref={waveSurferRef}
            audioUrl={effectiveAudioUrl}
            theme={theme}
            playbackRate={playbackRate}
          />
        </motion.div>
      </Box>

      <Box sx={styles.timeDisplay}>
        <Typography variant="caption" sx={styles.timeTextLeft}>
          {fmt(currentTime)}
        </Typography>
        <Typography variant="caption" sx={styles.timeTextRight}>
          {isLoaded ? fmt(duration) : "..."}
        </Typography>
      </Box>
    </Box>
  );
};
